/**
 * LEGACY VUE LOGGER — being phased out.
 *
 * The canonical frontend logger now lives at
 * `aven_frontend/aven_partner_ui/src/lib/logger.ts` (pino-browser + batched
 * transport + localStorage durability + retry). Once the partner rollout
 * proves stable in prod, this file will be carved into a Vue adapter that
 * delegates the wire layer to the new transport — preserving the `Logger`
 * class + `setupVueErrorHandler` API so Vue call sites don't change.
 *
 * aven_auto keeps using this legacy logger, so it is defined locally here
 * rather than in aven_shared. Don't make new improvements here. Fix bugs once
 * in the canonical impl, then port across when the adapter lands. See PR
 * #80759 for context.
 */
import * as Sentry from '@sentry/vue'
import isEmpty from 'lodash/isEmpty'
import utilInspect from 'util-inspect'
import axios, { AxiosInstance } from 'axios'
import { DEMOTED_ERROR_MESSAGES } from 'aven_shared/src/utils/otel/logger'

export const LogLevel = {
    // mirrors the levels used on the backend
    debug: 'DEBUG',
    log: 'INFO',
    info: 'INFO',
    warn: 'WARN',
    error: 'ERROR',
    fatal: 'FATAL',
}

/**
 * We use this as the error that we send to Sentry when our code only sends an error message,
 * but not a thrown Error (or Error subclass). For example:
 * logger.error('some message without an error')
 * As opposed to:
 * logger.error('some message with an error', error)
 *
 * Without this message wrapper, we can't actually group different Sentry messages because Sentry
 * automatically tries to group by stacktrace, which is not appropriate for error messages without
 * thrown errors.
 */
class SentryMessage extends Error {
    constructor(msg: string) {
        super(msg)
        this.name = 'SentryMessage'
        Object.setPrototypeOf(this, new.target.prototype)
    }
}

export const inspect = (msg: unknown) => {
    return utilInspect(msg as object, { breakLength: Infinity })?.replace(/[\s]*[\n][\s]*/g, '; ')
}

const ignoreErrors = [
    /(?=.*?(Max retry limit of 2 exceeded with error))(?=.*?(still_here))/gi, // This is a noisy sentry for when we retry sending the 'still_here' event which pings our backend frequently
]

interface LoggerOptions {
    avenProject: string
    sentryDSN: string
    logProcessorBaseUrl?: string
    clientSideLogsEnabled: boolean
    getMetadata: () => Record<string, string>
}

export class Logger {
    private readonly clientSideLogsEnabled: boolean
    private readonly avenProject: string
    private readonly loggerHttpClient?: AxiosInstance
    private isNetworkLoggingEnabled = true
    private readonly getMetadata: () => Record<string, string>

    constructor({ avenProject, sentryDSN, logProcessorBaseUrl, getMetadata, clientSideLogsEnabled }: LoggerOptions) {
        this.avenProject = avenProject
        this.getMetadata = getMetadata
        this.clientSideLogsEnabled = clientSideLogsEnabled

        if (logProcessorBaseUrl) {
            this.loggerHttpClient = axios.create({
                baseURL: logProcessorBaseUrl,
                headers: {
                    'Content-Type': 'application/json',
                    Accept: '*/*',
                },
                responseType: 'text',
                timeout: 10_000,
            })
        }
        const isScraper = /google|fbid|facebook|fbav|fb_/gi.test(navigator.userAgent)
        const isProdEnv = process.env.VUE_APP_NODE_ENV === 'production'
        // Strip http/https from URLs
        if (isProdEnv && !isScraper) {
            console.info('[INFO] Allowing logs to go to sentry')
            Sentry.init({
                dsn: sentryDSN,
                environment: process.env.VUE_APP_NODE_ENV,
                release: process.env.VUE_APP_SENTRY_RELEASE,
                maxValueLength: 1024 * 8,
                attachStacktrace: true,
                defaultIntegrations: false,
                ignoreErrors,
                integrations: [Sentry.functionToStringIntegration(), Sentry.inboundFiltersIntegration(), Sentry.breadcrumbsIntegration(), Sentry.linkedErrorsIntegration()],
                initialScope: {
                    tags: {
                        // add any global static tags here
                        avenProject: this.avenProject,
                    },
                },
            })
        } else {
            console.info('[INFO] Refusing to log development and/or Googlebot to sentry')
        }

        window.onunhandledrejection = (event: PromiseRejectionEvent) => {
            // See: https://blog.francium.tech/vue-lazy-routes-loading-chunk-failed-9ee407bbd58
            if (/Loading.*chunk.*failed./i.test(event?.reason?.message || '')) {
                this.info('Reloading page to fix stale chunk error')
                this.logMessageToSentry(`window.onunhandledrejection error: Reloading page to fix stale chunk error: ${inspect(event.reason)}`, 'warning', null, {
                    isPossibleNetworkIssue: true,
                })
                return window.location.reload()
            }

            const reason = event.reason
            const msg = `window.onunhandledrejection error: ${inspect(reason)}`
            if (DEMOTED_ERROR_MESSAGES.some((regex) => regex.test(String(reason)))) {
                this.warn(msg, reason instanceof Error ? reason : undefined, event)
            } else {
                this.error(msg, reason instanceof Error ? reason : undefined, event)
            }
        }

        window.onerror = (message, source, lineno, colno, error) => {
            const msg = `window.onerror error: ${inspect({ message, source, lineno, colno, error })}`
            if (DEMOTED_ERROR_MESSAGES.some((regex) => regex.test(String(message as string)))) {
                this.warn(msg, error, message)
            } else {
                this.error(msg, error, message)
            }
        }
    }

    public debug = (message: string) => {
        if (this.clientSideLogsEnabled) {
            console.debug(message)
        }
        void this.trySendToBackend({ message, level: LogLevel.debug })
    }

    public log = (message: string) => {
        if (this.clientSideLogsEnabled) {
            console.log(message)
        }
        void this.trySendToBackend({ message, level: LogLevel.log })
    }

    public info = (message: string) => {
        if (this.clientSideLogsEnabled) {
            console.info(message)
        }
        void this.trySendToBackend({ message, level: LogLevel.info })
    }

    public warn = (message: string, error?: any, event?: Event | PromiseRejectionEvent | string) => {
        if (this.clientSideLogsEnabled) {
            console.warn(message, error, event)
        }
        void this.trySendToBackend({ message, event, level: LogLevel.warn, error })
    }

    public error = (message: string, error?: any, event?: Event | PromiseRejectionEvent | string) => {
        try {
            message = this.formatMessage(message)
            if (!message) {
                return // Don't send empty messages
            }

            // Redirect network errors to warn() so we don't trigger a high-severity Sentry and
            // page the on-call engineer
            if (message?.includes('Network Error')) {
                this.warn(message, error, event)
                return
            }

            if (this.clientSideLogsEnabled) {
                console.error(message, event, error)
            }
        } catch (e) {
            message = `NOTE: Failed to pre-process logger.error message due to error ${e}! ` + message
        }

        void this.trySendToBackend({ message, event, level: LogLevel.error, error })
    }

    public fatal = (message: string, error?: any, event?: Event | PromiseRejectionEvent | string) => {
        try {
            message = this.formatMessage(message)
            if (!message) {
                return // Don't send empty messages
            }

            // Redirect network errors to error() so we don't trigger a high-severity Sentry and
            // page the on-call engineer
            if (message?.includes('Network Error')) {
                this.error(message, error, event)
                return
            }

            if (this.clientSideLogsEnabled) {
                console.error(message, event, error)
            }
        } catch (e) {
            message = `NOTE: Failed to pre-process logger.fatal message due to error ${e}! ` + message
        }

        void this.trySendToBackend({ message, event, level: LogLevel.fatal, error })
    }

    private logMessageToProcessor = async (message: string, levelLabel = 'INFO', error?: any): Promise<void> => {
        if (!this.loggerHttpClient) {
            // This does not work in dev but console logs will still be available in the browser
            // so not a huge reason to dupe them here
            return
        }
        const metadata = { source: this.avenProject, ...this.getMetadata() }
        await this.loggerHttpClient.post('/', { levelLabel, message, metadata, error: error ? inspect(error) : undefined })
    }

    private trySendToBackend = async ({ message, level, error, event }: { message: string; event?: Event | PromiseRejectionEvent | string; level?: string; error?: any }) => {
        message = this.formatMessage(message)
        if (!message || !this.isNetworkLoggingEnabled) {
            return // Don't send empty messages
        }

        try {
            if (typeof event === 'string') {
                message += `\tEvent: ${event}`
            } else if (typeof event !== 'undefined') {
                message += `\tEventJSON: ${JSON.stringify(event)}`
            }
        } catch (e) {
            message = `NOTE: Failed to append some items to message due to error ${e}! ` + message
        }

        try {
            await this.logMessageToProcessor(message, level, error)

            if (level === LogLevel.error || level === LogLevel.fatal) {
                const severity: Sentry.SeverityLevel = level === LogLevel.error ? 'error' : 'fatal'
                this.logMessageToSentry(message, severity, error)
            } else {
                let severity: Sentry.SeverityLevel
                if (level === LogLevel.info) {
                    severity = 'info'
                } else if (level === LogLevel.log) {
                    severity = 'info'
                } else {
                    severity = 'debug'
                }
                this.addLogBreadcrumbToSentry(severity, message)
            }
        } catch (error) {
            if (this.clientSideLogsEnabled) {
                console.error(`Could not log message to backend due to error: ${inspect(error as object)}\nThe message was ${message}`)
            }
            this.logMessageToSentry(`Could not log message to backend: ${message}`, 'error', error, { isPossibleNetworkIssue: true })
        }
    }

    /**
     * Breadcrumbs are used to create a trail of events prior to an issue.
     * https://docs.sentry.io/platforms/javascript/enriching-events/breadcrumbs/
     */
    private addLogBreadcrumbToSentry = (severity: Sentry.SeverityLevel, message: string) => {
        Sentry.addBreadcrumb({
            // https://develop.sentry.dev/sdk/event-payloads/breadcrumbs/#breadcrumb-types
            type: 'info',
            level: severity,
            // We need to truncate breadcrumb messages because Sentry will throw a 413 Payload too large
            // error in certain instances since we create very large log messages. NOTE THAT THIS IS NOT
            // TANTAMOUNT TO TRUNCATING LOGS! The logs are still fully available in CloudWatch.
            message: message?.slice(0, 256),
        })
    }

    private logMessageToSentry(message: string, severity: Sentry.SeverityLevel, error?: any, tags?: object) {
        try {
            const captureContext: any = {
                level: severity ?? 'fatal',
                extra: {
                    message,
                    // @ts-ignore navigator connection hasn't been standardized yet.
                    networkQuality: `type: ${navigator?.connection?.effectiveType}, downlink: ${navigator?.connection?.downlink}, online: ${navigator?.onLine}`,
                },
                tags: {
                    ...this.getMetadata(),
                    ...tags,
                },
            }
            const errorToSend = error ?? new SentryMessage(message)
            Sentry.captureException(errorToSend, captureContext)
        } catch (e) {
            Sentry.captureException(new Error(`Failed to capture exception due to error: ${e}\nException was ${message}`))
        }
    }

    private formatMessage = (message: string): string => {
        if (!message) {
            return ''
        }
        if (typeof message !== 'string') {
            if (isEmpty(message)) {
                return ''
            }
            return inspect(message)
        }

        return message.trim()
    }

    public setNetworkLogging = (enabled: boolean) => {
        this.isNetworkLoggingEnabled = enabled
    }

    public setupVueErrorHandler = (appOrVue: any /* Vue 2 or Vue 3 instance */): void => {
        appOrVue.config.errorHandler = (err: any) => {
            // Can't serialize 'vm'/'instance', which is a recursively defined Vue object (also no point)
            // 'info' isn't that useful, only has a short string description
            // err is the actual stack trace
            const msg = `vue error: ${err}\t${err.stack}`
            if (DEMOTED_ERROR_MESSAGES.some((regex) => regex.test(String(err.message)))) {
                this.warn(msg, err)
            } else {
                this.error(msg, err)
            }
        }
    }
}
